import { Str } from '@supercharge/strings/dist';
import { storeToRefs } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { computed, WritableComputedRef } from 'vue';
import Form from '~~/classes/Form';

export interface FormInputOptionalProps {
    label?: string;
    inlineLabel?: boolean;
    placeholder?: string;
    elementClassName?: string;
    containerClassName?: string;
    className?: string;
    variant?: string;
    inputId?: string;
    inputType?: string;
    step?: number;
    disabled?: boolean;
    withoutLabel?: boolean;
    tooltip?: string;
    autofocus?: boolean;
    required?: boolean;
    translated?: boolean;
    /**
     * Externally-controlled display locale for a `translated` field (TDD-3784).
     *
     * ADDITIVE: absent, the field keeps its own locale exactly as before —
     * initialised on the connected user's locale and changed only from its own
     * flag pill. Pass it (with `v-model:locale`) to make several fields share a
     * single locale, as the Presentation block on Ma page does. Nothing else in
     * the app passes it, so no other screen changes behaviour.
     */
    locale?: string;
    /**
     * Restricts the locale pill to this set of Trustup Pro locales (`be-fr`, …).
     *
     * ADDITIVE: absent, the pill lists every locale of every country, as before.
     */
    localeOptions?: string[];
    withVariables?: boolean;
    filledByAi?: boolean;
    size?: 'xl' | 'l' | 'm' | 's';
    validated?: boolean;
    themeKey?: string;
    /**
     * Selects the field's content when it receives focus, so typing replaces the
     * value instead of appending to it.
     *
     * For numeric fields that carry a meaningful default (a quantity of 1, a price of
     * 0,00 €) the default is a real value, not a placeholder: tapping such a field on
     * a phone puts the caret after the digits and the next keystroke produces "10"
     * instead of "0". There is no caret placement to preserve on a touch screen, so
     * selecting is strictly better there.
     *
     * Opt-in rather than automatic: on desktop, clicking into the middle of a number
     * to fix one digit is a real gesture, and selecting everything would break it.
     */
    selectOnFocus?: boolean;
}

export interface FormInputProps extends FormInputOptionalProps {
    form: Form<any>;
    model?: WritableComputedRef<any>;
    inputName: string;
    withNullOption?: boolean;
}

export interface FormInputData {
    props: FormInputProps;
    form: Form;
    model: WritableComputedRef<any>;
    label: ComputedRef<string>;
    inlineLabel: boolean;
    placeholder: ComputedRef<string>;
    inputId: ComputedRef<string>;
    inputName: string;
    inputType: string;
    step: number;
    className: ComputedRef<string | (string | object)[]>;
    labelClassName: ComputedRef<string>;
    elementClassName: ComputedRef<string>;
    containerClassName: ComputedRef<string>;
    errorClassName: ComputedRef<string>;
    labelContainerClassName: ComputedRef<string>;
    disabled: boolean;
    withoutLabel: boolean;
    tooltip: string | null;
    autofocus: boolean;
    required: boolean;
    translated: boolean;
    hasTranslation: (translationLocale: string) => boolean;
    locale: Ref<string>;
    localeOptions: string[] | undefined;
    elementKey: ComputedRef<string>;
    withVariables: boolean;
    size: string | undefined;
    validated: boolean;
}

export default function useFormInput(props: Readonly<Omit<FormInputProps, never>>, defaultProps?: FormInputOptionalProps) {
    const { $theme } = useNuxtApp();
    const { t } = useTranslation();

    /**
     * In case of translated inputs, we need to add the locale to the input name to make it unique.
     */
    const getFinalInputName = () => {
        return props.translated ? `${getFinalInputLabelKey()}.${locale.value}` : getFinalInputLabelKey();
    };

    /**
     * We are only interested in the last portion of the input name.
     * The dot notation is used to indicate nested objects.
     */
    const getFinalInputLabelKey = () => {
        return props.inputName?.split('.').pop() ?? '';
    };

    const themeKey = (base: string) => (props.variant ? `form.${props.variant}.${base}` : `form.${base}`);

    const className = computed(() => {
        if (props.className) return props.className;
        if (defaultProps?.className) return defaultProps.className;
        if (props.variant) {
            const c = $theme(themeKey('input'));
            return props.form.errors?.has(props.inputName) ? [c, '!border-red-600'] : c;
        }

        const size = props.size || defaultProps?.size;

        // New DS sizing system: when size is provided, use sized classes
        if (size) {
            const themeKey = defaultProps?.themeKey || 'input';
            const sizedClasses = props.disabled ? $theme(`form.input_disabled_sized.${size}`) : $theme(`form.${themeKey}_sized.${size}`);

            const classes = [sizedClasses];

            if (!props.disabled && props.form?.errors?.has(props.inputName)) {
                classes.push($theme('form.input_error_border'));
            } else if (!props.disabled && props.validated) {
                classes.push($theme('form.input_validated_border'));
            }

            if (props.filledByAi) {
                classes.push($theme('form.filled_ai'));
            }

            return classes;
        }

        // Legacy styles (no size prop)
        const baseClass = props.disabled
            ? $theme('form.input_disabled')
            : [
                  $theme('form.input'),
                  {
                      '!border-red-600': props.form?.errors?.has(props.inputName),
                  },
              ];

        if (props.filledByAi) {
            const aiClass = $theme('form.filled_ai');
            if (Array.isArray(baseClass)) {
                return [...baseClass, aiClass];
            }
            return [baseClass, aiClass];
        }

        return baseClass;
    });
    const size = props.size || defaultProps?.size;

    const elementClassName = computed(() => {
        return props.elementClassName || $theme(themeKey('containers.element'));
    });
    const containerClassName = computed(() => {
        return props.containerClassName || $theme(themeKey('containers.input'));
    });

    // Label styling, variant-aware via themeKey (mirrors className/elementClassName).
    // No-variant path returns the SAME strings as before this change, so in-app forms are unaffected.
    const labelClassName = computed(() => {
        if (props.inlineLabel) return $theme('form.label_inline'); // no variant defines an inline label
        if (props.variant) return $theme(themeKey('label')); // e.g. form.inbound.label
        return $theme('form.label') + ' self-end'; // unchanged default
    });
    const errorClassName = computed(() => $theme(themeKey('error')));
    const labelContainerClassName = computed(() => $theme(themeKey('containers.label')));

    // const style = computed<Record<string, string> | null>(() => {
    //     if (props.filledByAi) {
    //         return {
    //             border: '1px solid',
    //             borderImageSource: 'linear-gradient(89.57deg, #8A38F5 0%, #E839E5 51.92%, #E8B102 100%)',
    //             borderImageSlice: '1',
    //         };
    //     }
    //     return null;
    // });
    const label = computed(() => {
        return props.label || t(`form.labels.${getFinalInputLabelKey()}`);
    });
    const placeholder = computed(() => {
        return props.placeholder || label.value;
    });
    const inputId = computed(() => {
        return props.inputId || uuidv4();
    });
    const { authed } = storeToRefs(useAuthStore());
    // The field owns its locale. `props.locale`, when a parent supplies one, just
    // seeds and then drives it — the ref stays the single source of truth so the
    // flag pill keeps working and untouched screens keep their exact behaviour.
    const locale = ref(props.locale ?? authed.value?.locale ?? useGetDefaultLocale());

    watch(
        () => props.locale,
        (controlled) => {
            if (controlled && controlled !== locale.value) locale.value = controlled;
        }
    );

    const elementKey = computed(() => {
        return Str(props.inputName).slug().append(locale.value).get();
    });

    const model = computed({
        get() {
            if (!props.form) return '';
            if (props.translated) {
                return props.form.get(`${props.inputName}.${locale.value}`);
            }
            return props.form.get(props.inputName);
        },
        set(value) {
            if (!props.form) return;
            if (props.translated) {
                return props.form.set(`${props.inputName}.${locale.value}`, value);
            }
            return props.form.set(props.inputName, value);
        },
    });

    const hasTranslation = (translationLocale: string) => {
        return props.translated && props.form?.get(`${props.inputName}.${translationLocale}`);
    };

    const data: FormInputData = {
        props,
        model,
        form: props.form,
        className,
        labelClassName,
        elementClassName,
        containerClassName,
        errorClassName,
        labelContainerClassName,
        label,
        placeholder,
        inlineLabel: props.inlineLabel ?? false,
        inputId,
        inputName: props.inputName,
        inputType: props.inputType ?? 'text',
        step: props.step ?? 1,
        disabled: props.disabled,
        withoutLabel: props.withoutLabel ?? false,
        tooltip: props.tooltip ?? null,
        autofocus: props.autofocus ?? false,
        required: props.required ?? false,
        translated: props.translated ?? false,
        hasTranslation,
        locale,
        localeOptions: props.localeOptions,
        elementKey,
        withVariables: props.withVariables ?? false,
        size,
        validated: props.validated ?? false,
    };

    return data;
}

export { FormInputProps, FormInputData, useFormInput };
